import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Radio, Select, Col, Row, Input, Checkbox, Space, Switch, Progress, Tooltip } from "antd";
import axios from "axios";
import configData from "../../../config/config.json";
import constantData from "../../../constants/constantData.json";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { notifyfailure, notifycatch, notifysuccess } from "../../../utils/toast";
import loaderIcon from '../../../assets/images/icons/loader.gif';
import uploadIcon from '../../../assets/images/icons/upload-cloud.svg';
import FileIcon from '../../../assets/images/icons/offcanva_file_icon.svg';
import deleteIcon from '../../../assets/images/icons/trash.svg';
import DownloadOrangeIcon from '../../../assets/images/icons/download_csv_icon.svg';
import { Link } from "react-router-dom";
import { useOnboardingStep } from '../../../context/OnboardingStepContext';

const ACTIVATION_OPTIONS = [
    "Activation is possible using a separate tag",
    "Activation is possible at a higher floor price",
    "Otherwise, reach out to me for clarification",
];

const SENSITIVE_BLOCK_IDS = [
    "IAB25-1", "IAB25-2", "IAB25-3", "IAB25-4", "IAB25-5", "IAB25-6", "IAB25-7",
    "IAB26-1", "IAB26-2", "IAB26-3", "IAB26-4",
    "IAB9-9", "IAB8-18", "IAB8-5", "IAB11-4",
];

const onboardingSteps = [
    { title: 'Basic Details', description: 'Company & contact info' },
    { title: 'Ad Formats', description: 'Formats & integration method' },
    { title: 'Compliance & Safety', description: 'Regulations & brand safety' },
    { title: 'Inventory', description: 'Upload your inventory' },
];

// ─── CategoryRows (copied from MyProfileV2) ────────────────────────────
function CategoryRows({
    node,
    selected,
    setSelected,
    searchTerm = "",
    isDisabled,
    sensitive = {},
    setSensitive,
    attemptedIds = new Set(),
    setSensitiveAttemptedIds,
}) {
    const [open, setOpen] = useState(false);
    const hasChildren = node.children && node.children.length > 0;
    const childIds = node.children?.map(c => c.id) || [];
    const allChildrenSelected = hasChildren && childIds.every(id => selected.has(id));
    const someChildrenSelected = hasChildren && childIds.some(id => selected.has(id)) && !allChildrenSelected;
    const parentChecked = hasChildren ? allChildrenSelected : selected.has(node.id);
    const parentActive = hasChildren ? (allChildrenSelected || someChildrenSelected) : selected.has(node.id);

    const parentMatches = node.name.toLowerCase().includes(searchTerm);
    const childMatches = (node.children || []).filter(c => c.name.toLowerCase().includes(searchTerm));

    useEffect(() => {
        if (!hasChildren) return;
        if (!parentActive) setOpen(false);
    }, [parentActive, hasChildren]);

    useEffect(() => {
        if (!hasChildren) return;
        if (searchTerm) setOpen(childMatches.length > 0);
    }, [searchTerm, hasChildren, childMatches.length]);

    const toggleParent = () => {
        setSelected(prev => {
            const next = new Set(prev);
            if (hasChildren) {
                if (someChildrenSelected || allChildrenSelected) {
                    childIds.forEach(id => next.delete(id));
                } else {
                    childIds.forEach(id => next.add(id));
                }
            } else {
                if (next.has(node.id)) next.delete(node.id);
                else next.add(node.id);
            }
            return next;
        });
    };

    const toggleChild = (childId) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(childId)) next.delete(childId);
            else next.add(childId);
            return next;
        });
    };

    const handleSensitiveCheck = (id) => {
        setSensitive(prev => {
            const current = prev[id] || { isChecked: false, selection: "" };
            return {
                ...prev,
                [id]: { isChecked: !current.isChecked, selection: !current.isChecked ? current.selection : "" },
            };
        });
    };

    const handleSensitiveSelect = (id, value) => {
        setSensitive(prev => ({
            ...prev,
            [id]: { ...(prev[id] || { isChecked: true }), selection: value },
        }));
    };

    if (searchTerm && !parentMatches && childMatches.length === 0) return null;

    return (
        <>
            <tr className={`${parentActive ? "bottom_padding_cls" : ""} ${someChildrenSelected ? "is-partial" : ""}`}>
                <td className="cell checkbox-col">
                    <Switch
                        className={someChildrenSelected ? "switch-partial" : ""}
                        checked={parentChecked}
                        onChange={() => {
                            if (someChildrenSelected || allChildrenSelected || selected.has(node.id)) {
                                setSensitive(prev => {
                                    const next = { ...prev };
                                    delete next[node.id];
                                    childIds.forEach(id => delete next[id]);
                                    return next;
                                });
                                setSensitiveAttemptedIds(prev => {
                                    const next = new Set(prev);
                                    next.delete(node.id);
                                    childIds.forEach(id => next.delete(id));
                                    return next;
                                });
                            }
                            toggleParent();
                        }}
                    />
                </td>
                <td className="cell name-col td_with_select_checkbox">
                    {hasChildren ? (
                        <button type="button" onClick={() => setOpen(o => !o)}>
                            {open
                                ? <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1.5L6 6.5L11 1.5" stroke="#98A2B3" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                : <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 11L6.5 6L1.5 1" stroke="#98A2B3" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            }
                        </button>
                    ) : <></>}
                    <span className={`label ${!hasChildren ? "no-children m-0" : ""}`}>{node.name}</span>
                    {parentActive &&
                        <>
                            <div className="d-flex align-items-center">
                                <div className='primary_checkbox vertical_space_cls'>
                                    <Checkbox
                                        id={`checkbox_sensitive_${node.id}`}
                                        name={`checkbox_sensitive_${node.id}`}
                                        checked={sensitive[node.id]?.isChecked || false}
                                        onChange={() => handleSensitiveCheck(node.id)}
                                    >
                                        <span className="form_label">Allow under Custom condition</span>
                                    </Checkbox>
                                </div>
                                {sensitive[node.id]?.isChecked && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: '1' }} className='m_end_1'>
                                        <Select
                                            className="form_select basic no_search"
                                            showSearch={false}
                                            placeholder="Select your custom requirement to allow sensitive category"
                                            value={sensitive[node.id]?.selection || undefined}
                                            onChange={(val) => handleSensitiveSelect(node.id, val)}
                                            options={ACTIVATION_OPTIONS.map(opt => ({ label: opt, value: opt }))}
                                        />
                                    </div>
                                )}
                            </div>
                            {sensitive[node.id]?.isChecked && (
                                <>
                                    {attemptedIds.has(node.id) && !sensitive[node.id]?.selection && (
                                        <span className="error_content">This field is required</span>
                                    )}
                                </>
                            )}
                        </>
                    }
                </td>
            </tr>
            {open &&
                (node.children || [])
                    .filter(ch => !searchTerm || ch.name.toLowerCase().includes(searchTerm))
                    .map(ch => (
                        <tr key={ch.id} className={selected.has(ch.id) ? "is-selected" : ""}>
                            <td className="cell checkbox-col" />
                            <td className="cell name-col child">
                                <Switch
                                    checked={selected.has(ch.id)}
                                    onChange={() => toggleChild(ch.id)}
                                />
                                <span className="label no-children m-0">{ch.name}</span>
                            </td>
                        </tr>
                    ))
            }
        </>
    );
}


// ═══════════════════════════════════════════════════════════════════════════
// OnboardingStep3  –  Tabbed Onboarding Form
// ═══════════════════════════════════════════════════════════════════════════
const OnboardingStep3 = (props) => {

    const { TextArea } = Input;
    const { internalStep, setInternalStep, setPubStatus, registerInternalStep, setRegisterInternalStep } = useOnboardingStep();

    const isRegisterFlow = props.callFrom === "register";
    const initialActiveStep = isRegisterFlow
        ? (registerInternalStep && registerInternalStep <= 4 ? registerInternalStep : 1)
        : (internalStep && internalStep <= 4) ? internalStep : 1;
    const initialCompletedSteps = isRegisterFlow
        ? (registerInternalStep >= 5 ? [1, 2, 3, 4] : Array.from({ length: initialActiveStep - 1 }, (_, i) => i + 1))
        : (internalStep >= 5 ? [1, 2, 3, 4] : Array.from({ length: initialActiveStep - 1 }, (_, i) => i + 1));

    // ─── Tab state ──────────────────────────────────────────────────────
    const [activeStep, setActiveStep] = useState(initialActiveStep);
    const [highestStep, setHighestStep] = useState(initialActiveStep);
    const [completedSteps, setCompletedSteps] = useState(initialCompletedSteps);
    const progressPercent = Math.round((completedSteps.length / (onboardingSteps.length - 1)) * 100);

    const canFreelyNavigate = internalStep >= 4 || registerInternalStep >= 4;

    const selectStep = (stepIndex) => {
        if (!canFreelyNavigate && stepIndex >= activeStep) return;
        setActiveStep(stepIndex);
    };

    // ─── Disabled / status state ────────────────────────────────────────
    const [isDisabled, setIsDisabled] = useState(false);
    const [currentStatus, setCurrentStatus] = useState("");

    // ─── Button state ───────────────────────────────────────────────────
    const [buttonValue, setButtonValue] = useState(false);

    // ═══════════════════════════════════════════════════════════════════
    //  TAB 0 – Basic Details state
    // ═══════════════════════════════════════════════════════════════════
    const [name, setName] = useState("");
    const [nameIsRequiredError, setNameIsRequiredError] = useState(false);
    const [poc, setPoc] = useState("");
    const [pocIsRequiredError, setPocIsRequiredError] = useState(false);
    const [companyName, setCompanyName] = useState("");
    const [companyNameIsRequiredError, setCompanyNameIsRequiredError] = useState(false);
    const [email, setEmail] = useState("");
    const [companyWebsite, setCompanyWebsite] = useState("");
    const [companyWebsiteIsRequiredError, setCompanyWebsiteIsRequiredError] = useState(false);
    const [companyWebsiteUrlError, setCompanyWebsiteUrlError] = useState(false);
    const [im, setIm] = useState("");
    const [imIsRequiredError, setImIsRequiredError] = useState(false);
    const [selectedIm, setSelectedIm] = useState("telegram");
    const [countryOptions, setCountryOptions] = useState([]);
    const [country, setCountry] = useState("");
    const [countryIsRequiredError, setCountryIsRequiredError] = useState(false);
    const [companyLinkedin, setCompanyLinkedin] = useState("");
    const [companyLinkedinUrlError, setCompanyLinkedinUrlError] = useState(false);

    const [defineYourself, setDefineYourself] = useState([]);
    const [defineYourselfIsRequiredError, setDefineYourselfIsRequiredError] = useState(false);

    const [isMcm, setIsMcm] = useState(null);
    const [isMcmIsRequiredError, setIsMcmIsRequiredError] = useState(false);
    const [gamEmail, setGamEmail] = useState("");
    const [gamEmailIsRequiredError, setGamEmailIsRequiredError] = useState(false);
    const [gamEmailUrlError, setGamEmailUrlError] = useState(false);
    const [gamNetworkId, setGamNetworkId] = useState("");
    const [gamNetworkIdIsRequiredError, setGamNetworkIdIsRequiredError] = useState(false);

    const [inventoryMonetize, setInventoryMonetize] = useState([]);
    const [inventoryMonetizeIsRequiredError, setInventoryMonetizeIsRequiredError] = useState(false);
    const [inventoryMonetizeOther, setInventoryMonetizeOther] = useState("");
    const [inventoryMonetizeOtherIsRequiredError, setInventoryMonetizeOtherIsRequiredError] = useState(false);
    const [inventoryMonetizeWebsiteUserSync, setInventoryMonetizeWebsiteUserSync] = useState(null);
    const [inventoryMonetizeWebsiteUserSyncIsRequiredError, setInventoryMonetizeWebsiteUserSyncIsRequiredError] = useState(false);
    const [inventoryMonetizeWebsiteBanner, setInventoryMonetizeWebsiteBanner] = useState(null);
    const [inventoryMonetizeWebsiteBannerIsRequiredError, setInventoryMonetizeWebsiteBannerIsRequiredError] = useState(false);

    // ═══════════════════════════════════════════════════════════════════
    //  TAB 1 – Ad Formats state
    // ═══════════════════════════════════════════════════════════════════
    const [adFormatMonetize, setAdFormatMonetize] = useState([]);
    const [adFormatMonetizeIsRequiredError, setAdFormatMonetizeIsRequiredError] = useState(false);
    const [adFormatMonetizeOther, setAdFormatMonetizeOther] = useState("");
    const [adFormatMonetizeOtherIsRequiredError, setAdFormatMonetizeOtherIsRequiredError] = useState(false);

    const [integrationType, setIntegrationType] = useState([]);
    const [integrationTypeIsRequiredError, setIntegrationTypeIsRequiredError] = useState(false);
    const [prebidQuestion, setPrebidQuestion] = useState("");
    const [prebidQuestionIsRequiredError, setPrebidQuestionIsRequiredError] = useState(false);
    const [ortbType, setOrtbType] = useState([]);
    const [ortbTypeIsRequiredError, setOrtbTypeIsRequiredError] = useState(false);
    const [ortbQuestion, setOrtbQuestion] = useState("");
    const [ortbQuestionIsRequiredError, setOrtbQuestionIsRequiredError] = useState(false);
    const [vastType, setVastType] = useState([]);
    const [vastTypeIsRequiredError, setVastTypeIsRequiredError] = useState(false);

    const [integrationProtocolPlayer, setIntegrationProtocolPlayer] = useState("");
    const [integrationProtocolMime, setIntegrationProtocolMime] = useState("");
    const [integrationProtocolMimeIsRequiredError, setIntegrationProtocolMimeIsRequiredError] = useState(false);
    const [integrationProtocolTimeout, setIntegrationProtocolTimeout] = useState("");
    const [integrationProtocolAdserver, setIntegrationProtocolAdserver] = useState("");
    const [integrationProtocolImpression, setIntegrationProtocolImpression] = useState(null);
    const [integrationProtocolBidrequest, setIntegrationProtocolBidrequest] = useState("");
    const [integrationProtocolUid, setIntegrationProtocolUid] = useState(null);
    const [integrationProtocolRichMedia, setIntegrationProtocolRichMedia] = useState(null);
    const [integrationProtocolRichMediaIsRequiredError, setIntegrationProtocolRichMediaIsRequiredError] = useState(false);
    const [integrationProtocolRichMediaCreative, setIntegrationProtocolRichMediaCreative] = useState([]);

    // ═══════════════════════════════════════════════════════════════════
    //  TAB 2 – Compliance & Safety state
    // ═══════════════════════════════════════════════════════════════════
    const [regulationTool, setRegulationTool] = useState("");
    const [regulationToolIsRequiredError, setRegulationToolIsRequiredError] = useState(false);
    const [regulationAdserver, setRegulationAdserver] = useState("");
    const [regulationAdserverIsRequiredError, setRegulationAdserverIsRequiredError] = useState(false);
    const [regulationParameter, setRegulationParameter] = useState([]);
    const [regulationParameterIsRequiredError, setRegulationParameterIsRequiredError] = useState(false);
    const [regulationParameterOther, setRegulationParameterOther] = useState("");
    const [regulationParameterOtherIsRequiredError, setRegulationParameterOtherIsRequiredError] = useState(false);

    // Sensitive categories
    const [DATA, setDATA] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [selectedTrack, setSelectedTrack] = useState(new Set());
    const [sensitive, setSensitive] = useState({});
    const [sensitiveAttemptedIds, setSensitiveAttemptedIds] = useState(new Set());
    const [search, setSearch] = useState("");
    const searchTerm = search.trim().toLowerCase();

    const blockAllChecked = SENSITIVE_BLOCK_IDS.every(id => selected.has(id));
    const handleBlockAll = () => {
        setSelected(prev => {
            const next = new Set(prev);
            if (blockAllChecked) {
                SENSITIVE_BLOCK_IDS.forEach(id => next.delete(id));
            } else {
                SENSITIVE_BLOCK_IDS.forEach(id => next.add(id));
            }
            return next;
        });
    };
    const visibleNodes = DATA.filter(node => {
        const parentMatches = node.name.toLowerCase().includes(searchTerm);
        const childMatches = (node.children || []).some(c => c.name.toLowerCase().includes(searchTerm));
        return !searchTerm || parentMatches || childMatches;
    });

    // ═══════════════════════════════════════════════════════════════════
    //  TAB 3 – Media Kit state
    // ═══════════════════════════════════════════════════════════════════
    const [mediakit, setMediakit] = useState([]);
    const [mediakit2, setMediakit2] = useState([]);
    const [importUrl, setImportUrl] = useState("");
    const [importUrlError, setImportUrlError] = useState(false);
    const [mediakitIsRequiredError, setMediakitIsRequiredError] = useState(false);


    // ═══════════════════════════════════════════════════════════════════
    //  Refs
    // ═══════════════════════════════════════════════════════════════════
    const basicDetailsRef = useRef(null);
    const defineYourselfRef = useRef(null);
    const mcmRef = useRef(null);
    const inventoryMonetizeRef = useRef(null);
    const adFormatMonetizeRef = useRef(null);
    const integrationTypeRef = useRef(null);
    const integrationProtocolDetailsRef = useRef(null);
    const regulationDetailsRef = useRef(null);
    const sensitiveDetailsRef = useRef(null);
    const importUrlRef = useRef(null);


    // ═══════════════════════════════════════════════════════════════════
    //  Validators
    // ═══════════════════════════════════════════════════════════════════
    const validateEmail = (email) => {
        return String(email).toLowerCase().match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
    };

    const validateWebsiteUrl = (websiteUrl) => {
        return String(websiteUrl).toLowerCase().match(/.+[.].+/);
    };

    const validateLinkedinUrl = (linkedinUrl) => {
        return String(linkedinUrl).toLowerCase().match(/^(https?:\/\/)?([^/]+\.)?(linkedin\.[a-zA-Z]+\/.*)$/);
    };


    // ═══════════════════════════════════════════════════════════════════
    //  onChange / onBlur handlers
    // ═══════════════════════════════════════════════════════════════════

    // Basic Details
    function onChangeName(event) { setNameIsRequiredError(false); setName(event.target.value); }
    function onBlurName(event) { if (event.target.value.trim().length === 0) setNameIsRequiredError(true); setName(event.target.value.trim()); }
    function onChangePoc(event) { setPocIsRequiredError(false); setPoc(event.target.value); }
    function onBlurPoc(event) { if (event.target.value.trim().length === 0) setPocIsRequiredError(true); setPoc(event.target.value.trim()); }
    function onChangeCompanyName(event) { setCompanyNameIsRequiredError(false); setCompanyName(event.target.value); }
    function onBlurCompanyName(event) { if (event.target.value.trim().length === 0) { setCompanyNameIsRequiredError(true); setCompanyName(""); } setCompanyName(event.target.value.trim()); }
    function onChangeIm(event) { setImIsRequiredError(false); setIm(event.target.value); }
    function onBlurIm(event) { if (event.target.value.trim().length === 0) setImIsRequiredError(true); setIm(event.target.value.trim()); }
    function onChangeSelectedIm(event) { setSelectedIm(event); }

    function onChangeCompanyWebsite(event) {
        setCompanyWebsite(event.target.value);
        if (event.target.value.length === 0) { setCompanyWebsiteUrlError(false); setCompanyWebsiteIsRequiredError(true); }
        else { setCompanyWebsiteIsRequiredError(false); if (validateWebsiteUrl(event.target.value) === null) setCompanyWebsiteUrlError(true); else setCompanyWebsiteUrlError(false); }
    }
    function onBlurCompanyWebsite(event) {
        if (event.target.value.trim().length === 0) { setCompanyWebsiteUrlError(false); setCompanyWebsiteIsRequiredError(true); }
        else { if (validateWebsiteUrl(event.target.value.trim()) === null) setCompanyWebsiteUrlError(true); else setCompanyWebsiteUrlError(false); }
        setCompanyWebsite(event.target.value.trim());
    }

    function onChangeCompanyLinkedin(event) {
        setCompanyLinkedin(event.target.value);
        if (event.target.value.length === 0) setCompanyLinkedinUrlError(false);
        else { if (validateLinkedinUrl(event.target.value) === null) setCompanyLinkedinUrlError(true); else setCompanyLinkedinUrlError(false); }
    }
    function onBlurCompanyLinkedin(event) {
        if (event.target.value.trim().length === 0) setCompanyLinkedinUrlError(false);
        else { if (validateLinkedinUrl(event.target.value.trim()) === null) setCompanyLinkedinUrlError(true); else setCompanyLinkedinUrlError(false); }
        setCompanyLinkedin(event.target.value.trim());
    }

    function onChangeDefineYourself(event) {
        if (event.length === 0) setDefineYourselfIsRequiredError(true);
        else setDefineYourselfIsRequiredError(false);
        setDefineYourself(event);
    }
    function onChangeIsMcm(event) { setIsMcmIsRequiredError(false); setIsMcm(event.target.value); }
    function onChangeGamEmail(event) {
        setGamEmailIsRequiredError(false); setGamEmail(event.target.value);
        if (validateEmail(event.target.value) === null) setGamEmailUrlError(true); else setGamEmailUrlError(false);
    }
    function onBlurGamEmail(event) {
        if (event.target.value.trim().length === 0) { setGamEmailUrlError(false); setGamEmailIsRequiredError(true); }
        else { if (validateEmail(event.target.value.trim()) === null) setGamEmailUrlError(true); else setGamEmailUrlError(false); }
        setGamEmail(event.target.value.trim());
    }
    function onChangeGamNetworkId(event) { setGamNetworkIdIsRequiredError(false); setGamNetworkId(event.target.value); }
    function onBlurGamNetworkId(event) { if (event.target.value.trim().length === 0) setGamNetworkIdIsRequiredError(true); setGamNetworkId(event.target.value.trim()); }

    function onChangeInventoryMonetize(event) {
        if (event.length === 0) setInventoryMonetizeIsRequiredError(true);
        else setInventoryMonetizeIsRequiredError(false);
        if (!event.includes("Others")) setInventoryMonetizeOtherIsRequiredError(false);
        setInventoryMonetize(event);
    }
    function onChangeInventoryMonetizeWebsiteUserSync(event) { setInventoryMonetizeWebsiteUserSyncIsRequiredError(false); setInventoryMonetizeWebsiteUserSync(event.target.value); }
    function onChangeInventoryMonetizeWebsiteBanner(event) { setInventoryMonetizeWebsiteBannerIsRequiredError(false); setInventoryMonetizeWebsiteBanner(event.target.value); }

    // Ad Formats
    function onChangeAdFormatMonetize(event) {
        if (event.length === 0) setAdFormatMonetizeIsRequiredError(true);
        else setAdFormatMonetizeIsRequiredError(false);
        if (!event.includes("Others")) setAdFormatMonetizeOtherIsRequiredError(false);
        setAdFormatMonetize(event);
    }
    function onChangeIntegrationType(event) {
        if (event.length > 0) setIntegrationTypeIsRequiredError(false);
        else setIntegrationTypeIsRequiredError(true);
        setIntegrationType(event);
    }
    function onChangePrebidQuestion(event) { setPrebidQuestionIsRequiredError(false); setPrebidQuestion(event.target.value); }
    function onBlurPrebidQuestion(event) { if (event.target.value.trim().length === 0) setPrebidQuestionIsRequiredError(true); setPrebidQuestion(event.target.value.trim()); }
    function onChangeOrtbType(event) { setOrtbType(event); if (event?.length === 0) setOrtbTypeIsRequiredError(true); else setOrtbTypeIsRequiredError(false); }
    function onChangeOrtbQuestion(event) { setOrtbQuestionIsRequiredError(false); setOrtbQuestion(event.target.value); }
    function onBlurOrtbQuestion(event) { if (event.target.value.trim().length === 0) setOrtbQuestionIsRequiredError(true); setOrtbQuestion(event.target.value.trim()); }
    function onChangeVastType(event) { setVastType(event); if (event?.length === 0) setVastTypeIsRequiredError(true); else setVastTypeIsRequiredError(false); }

    function onChangeIntegrationProtocolPlayer(event) { setIntegrationProtocolPlayer(event.target.value); }
    function onBlurIntegrationProtocolPlayer(event) { setIntegrationProtocolPlayer(event.target.value.trim()); }
    function onChangeIntegrationProtocolMime(event) { setIntegrationProtocolMimeIsRequiredError(false); setIntegrationProtocolMime(event.target.value); }
    function onBlurIntegrationProtocolMime(event) { if (event.target.value.trim().length === 0) setIntegrationProtocolMimeIsRequiredError(true); setIntegrationProtocolMime(event.target.value.trim()); }
    function onChangeIntegrationProtocolTimeout(event) { setIntegrationProtocolTimeout(event.target.value); }
    function onBlurIntegrationProtocolTimeout(event) { setIntegrationProtocolTimeout(event.target.value.trim()); }
    function onChangeIntegrationProtocolAdserver(event) { setIntegrationProtocolAdserver(event.target.value); }
    function onBlurIntegrationProtocolAdserver(event) { setIntegrationProtocolAdserver(event.target.value.trim()); }
    function onChangeIntegrationProtocolImpression(event) { setIntegrationProtocolImpression(event.target.value); }
    function onChangeIntegrationProtocolBidrequest(event) { setIntegrationProtocolBidrequest(event.target.value); }
    function onBlurIntegrationProtocolBidrequest(event) { setIntegrationProtocolBidrequest(event.target.value.trim()); }
    function onChangeIntegrationProtocolUid(event) { setIntegrationProtocolUid(event.target.value); }
    function onChangeIntegrationProtocolRichMedia(event) { setIntegrationProtocolRichMediaIsRequiredError(false); setIntegrationProtocolRichMedia(event.target.value); }
    function onChangeIntegrationProtocolRichMediaCreative(event) { setIntegrationProtocolRichMediaCreative(event); }

    // Compliance & Safety
    function onChangeRegulationTool(event) { setRegulationToolIsRequiredError(false); setRegulationTool(event.target.value); }
    function onBlurRegulationTool(event) { if (event.target.value.trim().length === 0) setRegulationToolIsRequiredError(true); setRegulationTool(event.target.value.trim()); }
    function onChangeRegulationAdserver(event) { setRegulationAdserverIsRequiredError(false); setRegulationAdserver(event.target.value); }
    function onBlurRegulationAdserver(event) { if (event.target.value.trim().length === 0) setRegulationAdserverIsRequiredError(true); setRegulationAdserver(event.target.value.trim()); }
    function onChangeRegulationParameter(event) {
        if (event.length === 0) setRegulationParameterIsRequiredError(true);
        else setRegulationParameterIsRequiredError(false);
        if (!event.includes("Others")) setRegulationParameterOtherIsRequiredError(false);
        setRegulationParameter(event);
    }

    // Media Kit
    function onChangeMediakit(e) {
        let count = 0;
        if (e.target.files.length > 0) {
            let tempMediaKit = [...mediakit];
            for (let file of e.target.files) {
                if (file.size / (1024 * 1024) < 100) {
                    tempMediaKit.push({ data: file, preview: URL.createObjectURL(file), filename: file.name, size: file.size });
                } else { count++; }
            }
            setMediakit(tempMediaKit);
            setMediakitIsRequiredError(false);
            const fileInput00 = document.getElementById("file000_step3");
            if (fileInput00) fileInput00.value = "";
            if (count !== 0) notifyfailure(`${count} files are rejected as they are failing validation (File should be Less than 100mb)`);
        }
    }

    const fetchAndDownloadFilesMediaKit = async (ob) => {
        try {
            const response = await fetch(ob.url);
            if (!response.ok) throw new Error(`Failed to fetch ${ob?.url}: ${response.statusText}`);
            const blob = await response.blob();
            const filename = `${ob.fileName}${ob.fileName.includes("." + ob?.ext) ? "" : `${"." + ob.ext}`}`;
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            notifysuccess("File downloaded successfully");
        } catch (error) {
            notifyfailure("Error while fetching the media kit.");
        }
    };

    function onChangeImportUrl(event) {
        setImportUrl(event.target.value);
        if (event.target.value.length === 0) setImportUrlError(false);
        else { setMediakitIsRequiredError(false); if (validateWebsiteUrl(event.target.value) === null) setImportUrlError(true); else setImportUrlError(false); }
    }
    function onBlurImportUrl(event) {
        if (event.target.value.trim().length === 0) setImportUrlError(false);
        else { if (validateWebsiteUrl(event.target.value.trim()) === null) setImportUrlError(true); else setImportUrlError(false); }
        setImportUrl(event.target.value.trim());
    }


    // ═══════════════════════════════════════════════════════════════════
    //  scrollToItem
    // ═══════════════════════════════════════════════════════════════════
    const scrollToItem = (ref, offset = 80) => {
        if (ref && ref.current) {
            ref.current.style.scrollMarginTop = `${offset}px`;
            ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
            setTimeout(() => { ref.current.style.scrollMarginTop = ''; }, 0);
        }
    };


    // ═══════════════════════════════════════════════════════════════════
    //  API: fetch details
    // ═══════════════════════════════════════════════════════════════════
    useEffect(() => {
        const fetchUrl = isRegisterFlow
            ? `${configData.SERVER_URL}ops/getPubDetails?_id=${props.pubId}`
            : `${configData.SERVER_URL}ops/getPubDetails`;
        const fetchHeaders = isRegisterFlow
            ? { "Content-Type": "application/json" }
            : { "Content-Type": "application/json", "x-access-token": Cookies.get("token_xapads_ssp") };

        const shouldFetch = isRegisterFlow || Cookies.get("token_xapads_ssp");
        if (shouldFetch) {
            axios
                .get(fetchUrl, { headers: fetchHeaders })
                .then((response) => {
                    const d = response?.data?.data;

                    setCurrentStatus(d?.status || "");

                    // Basic details
                    setName(d?.p_name || "");
                    setPoc(d?.poc || "");
                    setCompanyName(d?.o_name || "");
                    setCompanyWebsite(d?.c_website || "");
                    setEmail(d?.email || "");
                    setSelectedIm(d?.im?.label || "telegram");
                    setIm(d?.im?.value || "");
                    setCountry(d?.address?.country || "");
                    setCompanyLinkedin(d?.o_contact || "");
                    setDefineYourself(d?.defineYourself || []);
                    setIsMcm(d?.isMcm ?? null);
                    setGamEmail(d?.gamEmail || "");
                    setGamNetworkId(d?.gamNetworkCode || "");
                    setInventoryMonetize(d?.inventory_type_select || []);
                    setInventoryMonetizeOther(d?.inventory_type_select_other || "");
                    setInventoryMonetizeWebsiteUserSync(d?.inventory_type_select_website_usersync ?? null);
                    setInventoryMonetizeWebsiteBanner(d?.inventory_type_select_website_bannerPlacement ?? null);

                    // Ad formats
                    setAdFormatMonetize(d?.ad_format_monetise || []);
                    setAdFormatMonetizeOther(d?.ad_format_monetise_other || "");
                    setIntegrationType(d?.integration_type || []);
                    setPrebidQuestion(d?.prebidQuestion || "");
                    setOrtbQuestion(d?.ortbQuestion || "");
                    setOrtbType(d?.ortb_type || []);
                    setVastType(d?.vast_type || []);
                    setIntegrationProtocolPlayer(d?.integration_protocol_player || "");
                    setIntegrationProtocolMime(d?.integration_protocol_mime || "");
                    setIntegrationProtocolTimeout(d?.integration_protocol_timeout || "");
                    setIntegrationProtocolAdserver(d?.integration_protocol_adserver || "");
                    setIntegrationProtocolImpression(d?.integration_protocol_impression ?? null);
                    setIntegrationProtocolBidrequest(d?.integration_protocol_bidrequest || "");
                    setIntegrationProtocolUid(d?.integration_protocol_uid ?? null);
                    setIntegrationProtocolRichMedia(d?.integration_protocol_rich_media ?? null);
                    setIntegrationProtocolRichMediaCreative(d?.integration_protocol_rich_media_creative || []);

                    // Compliance & Safety
                    setRegulationTool(d?.regulation_brand_safety_tool || "");
                    setRegulationAdserver(d?.regulation_brand_safety_adserver || "");
                    setRegulationParameter(d?.regulation_brand_safety_regulator || []);
                    setRegulationParameterOther(d?.regulation_brand_safety_regulator_other || "");
                    setSelected(new Set(d?.blockCategory) || new Set());
                    setSelectedTrack(new Set(d?.blockCategory) || new Set());
                    setSensitive(d?.bcate_data || {});

                    // Media Kit
                    setMediakit2(d?.mediaKit || []);
                    setImportUrl(d?.media_link || "");

                    // Mark basic details as completed since step 1 already filled it
                    if (d?.p_name && d?.poc && d?.o_name && d?.c_website && d?.im?.value && d?.address?.country && d?.defineYourself?.length > 0 && d?.isMcm !== null && d?.isMcm !== undefined && d?.inventory_type_select?.length > 0) {
                        setCompletedSteps(prev => prev.includes(1) ? prev : [...prev, 1]);
                    }
                })
                .catch(() => {
                    notifyfailure("Failed to fetch the publisher details.");
                });
        }


    }, []);


    useEffect(() => {
        // Fetch country list
        setTimeout(() => {
            axios.get(configData.SERVER_URL + "user/countryList", { headers: { "Content-Type": "application/json" } })
                .then((response) => { setCountryOptions(response.data.data); })
                .catch(() => { });
        }, 400)

    }, [])

    useEffect(() => {
        // Fetch IAB categories
        setTimeout(() => {
            axios.get(configData.SERVER_URL + "user/iabCat", { headers: { "Content-Type": "application/json" } })
                .then((response) => { setDATA(response.data.data); })
                .catch(() => { });
        }, 500)
    }, [])

    // Normalize blockCategory
    useEffect(() => {
        if (!DATA || DATA.length === 0) return;
        setSelected(prev => {
            const next = new Set();
            prev.forEach(id => {
                const parent = DATA.find(cat => cat.id === id);
                if (parent && parent.children && parent.children.length > 0) {
                    parent.children.forEach(ch => next.add(ch.id));
                } else {
                    next.add(id);
                }
            });
            return next;
        });
    }, [DATA, selectedTrack]);

    // Ant select search class helper
    useEffect(() => {
        const inputs = document.querySelectorAll('.ant-select-selection-search-input');
        inputs.forEach(input => {
            const handleInput = () => {
                const parent = input.closest('.ant-select-selection-search');
                if (input.value.length > 0) parent.classList.add('searching');
                else parent.classList.remove('searching');
            };
            input.addEventListener('input', handleInput);
            return () => input.removeEventListener('input', handleInput);
        });
    });


    // ═══════════════════════════════════════════════════════════════════
    //  Build FormData from current state (for ALL filled tabs)
    // ═══════════════════════════════════════════════════════════════════
    const STEP_ENDPOINTS = {
        1: "ops/basic_setting",
        2: "ops/ad_format",
        3: "ops/compliance",
        4: "ops/media_kit",
    };

    const buildPayload = () => {
        switch (activeStep) {
            case 1: {
                let formData = new FormData();
                formData.append("p_name", name);
                formData.append("poc", poc);
                formData.append("o_name", companyName);
                formData.append("c_website", companyWebsite);
                formData.append("email", email);
                formData.append("im", JSON.stringify({ label: selectedIm, value: im }));
                formData.append("country", country);
                formData.append("o_contact", companyLinkedin);
                formData.append("defineYourself", JSON.stringify(defineYourself));
                formData.append("isMcm", isMcm);
                formData.append("gamEmail", gamEmail);
                formData.append("gamNetworkCode", gamNetworkId);
                formData.append("inventory_type_select", JSON.stringify(inventoryMonetize));
                formData.append("inventory_type_select_other", inventoryMonetizeOther);
                formData.append("inventory_type_select_website_usersync", inventoryMonetizeWebsiteUserSync);
                formData.append("inventory_type_select_website_bannerPlacement", inventoryMonetizeWebsiteBanner);
                formData.append("media_link", importUrl);
                mediakit.forEach((file) => { formData.append('mediaKit_new', file.data); });
                formData.append("mediaKit", JSON.stringify(mediakit2));
                formData.append("internal_step", activeStep);
                return formData;
            }

            case 2:
                return {
                    ad_format_monetise: JSON.stringify(adFormatMonetize),
                    ad_format_monetise_other: JSON.stringify(adFormatMonetizeOther),
                    integration_type: JSON.stringify(integrationType),
                    prebidQuestion,
                    ortbQuestion,
                    ortb_type: JSON.stringify(ortbType),
                    vast_type: JSON.stringify(vastType),
                    integration_protocol_player: integrationProtocolPlayer,
                    integration_protocol_mime: integrationProtocolMime,
                    integration_protocol_timeout: integrationProtocolTimeout,
                    integration_protocol_adserver: integrationProtocolAdserver,
                    integration_protocol_impression: integrationProtocolImpression,
                    integration_protocol_bidrequest: integrationProtocolBidrequest,
                    integration_protocol_uid: integrationProtocolUid,
                    integration_protocol_rich_media: integrationProtocolRichMedia,
                    integration_protocol_rich_media_creative: JSON.stringify(integrationProtocolRichMediaCreative),
                    internal_step: activeStep,
                };

            case 3: {
                const filteredSensitive = Object.fromEntries(Object.entries(sensitive).filter(([, v]) => v.isChecked));
                return {
                    regulation_brand_safety_tool: regulationTool,
                    regulation_brand_safety_adserver: regulationAdserver,
                    regulation_brand_safety_regulator: JSON.stringify(regulationParameter),
                    regulation_brand_safety_regulator_other: regulationParameterOther,
                    blockCategory: JSON.stringify([...selected]),
                    bcate_data: JSON.stringify(filteredSensitive),
                    internal_step: activeStep,
                };
            }

            case 4: {
                let formData = new FormData();
                formData.append("media_link", importUrl);
                mediakit.forEach((file) => { formData.append('mediaKit_new', file.data); });
                formData.append("mediaKit", JSON.stringify(mediakit2));
                formData.append("internal_step", activeStep);
                return formData;
            }

            default:
                return {};
        }
    };


    // ═══════════════════════════════════════════════════════════════════
    //  Tab-level validation
    // ═══════════════════════════════════════════════════════════════════

    const validateBasicDetails = () => {
        let valid = true;

        if (name.trim() === "") { setNameIsRequiredError(true); valid = false; }
        if (poc.trim() === "") { setPocIsRequiredError(true); valid = false; }
        if (companyName.trim() === "") { setCompanyNameIsRequiredError(true); valid = false; }
        if (companyWebsite.trim() === "") { setCompanyWebsiteIsRequiredError(true); valid = false; }
        if (companyWebsiteUrlError) valid = false;
        if (im.trim() === "") { setImIsRequiredError(true); valid = false; }
        if (companyLinkedinUrlError) valid = false;
        if (defineYourself.length === 0) { setDefineYourselfIsRequiredError(true); valid = false; }
        if (isMcm === null) { setIsMcmIsRequiredError(true); valid = false; }
        if (isMcm && gamEmail.trim() === "") { setGamEmailIsRequiredError(true); valid = false; }
        if (isMcm && gamNetworkId.trim() === "") { setGamNetworkIdIsRequiredError(true); valid = false; }
        if (isMcm && gamEmailUrlError) valid = false;
        if (inventoryMonetize.length === 0) { setInventoryMonetizeIsRequiredError(true); valid = false; }
        if (inventoryMonetize.includes("Website") && inventoryMonetizeWebsiteUserSync === null) { setInventoryMonetizeWebsiteUserSyncIsRequiredError(true); valid = false; }
        if (inventoryMonetize.includes("Website") && inventoryMonetizeWebsiteBanner === null) { setInventoryMonetizeWebsiteBannerIsRequiredError(true); valid = false; }
        if (inventoryMonetize.includes("Others") && inventoryMonetizeOther.trim() === "") { setInventoryMonetizeOtherIsRequiredError(true); valid = false; }

        if (!valid) {
            if (name.trim() === "" || poc.trim() === "" || companyName.trim() === "" || companyWebsite.trim() === "" || companyWebsiteUrlError || im.trim() === "" || companyLinkedinUrlError) scrollToItem(basicDetailsRef);
            else if (defineYourself.length === 0) scrollToItem(defineYourselfRef);
            else if (isMcm === null || (isMcm && (gamEmail.trim() === "" || gamNetworkId.trim() === ""))) scrollToItem(mcmRef);
            else if (inventoryMonetize.length === 0 || (inventoryMonetize.includes("Website") && (inventoryMonetizeWebsiteUserSync === null || inventoryMonetizeWebsiteBanner === null)) || (inventoryMonetize.includes("Others") && inventoryMonetizeOther.trim() === "")) scrollToItem(inventoryMonetizeRef);
        }

        return valid;
    };

    const validateAdFormats = () => {
        let valid = true;

        if (adFormatMonetize.length === 0) { setAdFormatMonetizeIsRequiredError(true); valid = false; }
        if (adFormatMonetize.includes("Others") && adFormatMonetizeOther.trim() === "") { setAdFormatMonetizeOtherIsRequiredError(true); valid = false; }
        if (integrationType.length === 0) { setIntegrationTypeIsRequiredError(true); valid = false; }
        if (integrationType.includes("Prebid") && prebidQuestion.trim() === "") { setPrebidQuestionIsRequiredError(true); valid = false; }
        if (integrationType.includes("ORTB") && ortbQuestion.trim() === "") { setOrtbQuestionIsRequiredError(true); valid = false; }
        if (integrationType.includes("ORTB") && ortbType.length === 0) { setOrtbTypeIsRequiredError(true); valid = false; }
        if (integrationType.includes("VAST") && vastType.length === 0) { setVastTypeIsRequiredError(true); valid = false; }
        if (integrationProtocolMime.trim() === "") { setIntegrationProtocolMimeIsRequiredError(true); valid = false; }
        if (integrationProtocolRichMedia === null) { setIntegrationProtocolRichMediaIsRequiredError(true); valid = false; }

        if (!valid) {
            if ((adFormatMonetize.length === 0) || (adFormatMonetize.includes("Others") && adFormatMonetizeOther.trim() === "")) scrollToItem(adFormatMonetizeRef);
            else if (integrationType.length === 0 || (integrationType.includes("Prebid") && prebidQuestion.trim() === "") || (integrationType.includes("ORTB") && (ortbQuestion.trim() === "" || ortbType.length === 0)) || (integrationType.includes("VAST") && vastType.length === 0)) scrollToItem(integrationTypeRef);
            else scrollToItem(integrationProtocolDetailsRef);
        }

        return valid;
    };

    const validateComplianceSafety = () => {
        let valid = true;

        if (regulationTool.trim() === "") { setRegulationToolIsRequiredError(true); valid = false; }
        if (regulationAdserver.trim() === "") { setRegulationAdserverIsRequiredError(true); valid = false; }
        if (regulationParameter.length === 0) { setRegulationParameterIsRequiredError(true); valid = false; }
        if (regulationParameter.includes("Others") && regulationParameterOther.trim() === "") { setRegulationParameterOtherIsRequiredError(true); valid = false; }

        // Sensitive category validation
        const checkedIds = Object.entries(sensitive).filter(([, v]) => v.isChecked).map(([id]) => id);
        setSensitiveAttemptedIds(new Set(checkedIds));
        const hasSensitiveValidationError = checkedIds.some(id => !sensitive[id]?.selection);
        if (hasSensitiveValidationError) {
            notifyfailure("Please select custom requirements for the checked sensitive categories.");
            scrollToItem(sensitiveDetailsRef);
            valid = false;
        }

        if (!valid && !hasSensitiveValidationError) {
            scrollToItem(regulationDetailsRef);
        }

        return valid;
    };

    const validateMediaKit = () => {
        if (importUrlError) return false;
        if ((mediakit.length + mediakit2.length) === 0 && importUrl.trim() === "") {
            setMediakitIsRequiredError(true);
            scrollToItem(importUrlRef);
            return false;
        }
        return true;
    };


    // ═══════════════════════════════════════════════════════════════════
    //  Save handler (per-tab)
    // ═══════════════════════════════════════════════════════════════════
    const handleSave = async (e) => {
        e.preventDefault();

        // Tab 4 (Inventory) - mark complete without API call
        // if (activeStep === 4) {
        //     setCompletedSteps(prev => prev.includes(4) ? prev : [...prev, 4]);
        //     const nextStep = 5;
        //     setHighestStep(prev => Math.max(prev, nextStep));
        //     if (!isRegisterFlow) setInternalStep(nextStep);
        //     else setRegisterInternalStep(nextStep);
        //     return;
        // }

        // Validate the current tab
        let isValid = false;
        switch (activeStep) {
            case 1: isValid = validateBasicDetails(); break;
            case 2: isValid = validateAdFormats(); break;
            case 3: isValid = validateComplianceSafety(); break;
            default: isValid = true;
        }

        if (!isValid) return;

        if (!isRegisterFlow && !Cookies.get("token_xapads_ssp")) return;

        setButtonValue(true);
        const token = isRegisterFlow ? null : Cookies.get("token_xapads_ssp");
        const queryParam = isRegisterFlow ? `?pubId=${props.pubId}` : "";

        const payload = buildPayload();
        const endpoint = `${configData.SERVER_URL}${STEP_ENDPOINTS[activeStep]}${queryParam}`;
        const isFormData = payload instanceof FormData;

        try {
            const response = await axios.put(endpoint, payload, {
                headers: {
                    "Content-Type": isFormData ? "multipart/form-data" : "application/json",
                    ...(token ? { "x-access-token": token } : {}),
                }
            });

            notifysuccess(response.data.message);

            // Mark current tab as completed and move to next
            setCompletedSteps(prev => prev.includes(activeStep) ? prev : [...prev, activeStep]);

            if (activeStep === 3) {
                const idForStatus = isRegisterFlow ? props.pubId : jwtDecode(token)._id;
                axios.get(configData.SERVER_URL + "user/status?_id=" + idForStatus)
                    .then((statusRes) => {
                        setCurrentStatus(statusRes.data.data.status);
                    })
                    .catch(() => { });
                setTimeout(() => {
                    const nextStep = activeStep + 1;
                    setActiveStep(nextStep);
                    setHighestStep(prev => Math.max(prev, nextStep));
                    setButtonValue(false);
                    if (!isRegisterFlow) {
                        setInternalStep(prev => Math.max(prev, nextStep));
                        setPubStatus("onboard");
                    } else {
                        setRegisterInternalStep(prev => Math.max(prev, nextStep));
                    }
                }, 500);
            } else if (activeStep < onboardingSteps.length - 1) {
                setTimeout(() => {
                    const nextStep = activeStep + 1;
                    setActiveStep(nextStep);
                    setHighestStep(prev => Math.max(prev, nextStep));
                    setButtonValue(false);
                    if (!isRegisterFlow) setInternalStep(prev => Math.max(prev, nextStep));
                    else setRegisterInternalStep(prev => Math.max(prev, nextStep));
                }, 500);
            } else {
                const nextStep = activeStep + 1;
                setHighestStep(prev => Math.max(prev, nextStep));
                setButtonValue(false);
                if (!isRegisterFlow) setInternalStep(prev => Math.max(prev, nextStep));
                else setRegisterInternalStep(prev => Math.max(prev, nextStep));
            }
        } catch (error) {
            if (error?.response) {
                if (error.response.data.message?.length !== 0) {
                    notifyfailure(error.response.data.message);
                }
            } else {
                notifycatch();
            }
            setButtonValue(false);
        }
    };


    // ═══════════════════════════════════════════════════════════════════
    //  RENDER
    // ═══════════════════════════════════════════════════════════════════
    return (
        <div className="tabs_forms">
            {/* ──── Left Sidebar ──── */}
            <div className='tabs_div'>
                <div className='tabs_inner_div'>
                    <h5>Onboarding</h5>
                    <h4>You're one step closer to activation</h4>

                    <div className='progress_bar_div'>
                        <div className='progress_bar_text'>
                            <span>Progress</span>
                            <p>{progressPercent}%</p>
                        </div>
                        {/* <Progress percent={progressPercent} showInfo={false} /> */}
                        <Progress percent={progressPercent} showInfo={false}
                            strokeColor={progressPercent === 100 ? "#00CD4C" : undefined}
                        />
                    </div>

                    <div className='custome_tabs_div'>
                        <nav className='onboarding_stepper' aria-label='Onboarding steps'>
                            {onboardingSteps.map((step, index) => {
                                const stepNum = index + 1;
                                const isComplete = completedSteps.includes(stepNum);
                                const isActive = activeStep === stepNum;
                                const canAccess = canFreelyNavigate || stepNum < activeStep;
                                const cannotAccess = !canAccess && !isActive;

                                return (
                                    <button
                                        key={step.title}
                                        type='button'
                                        className={`onboarding_step ${isActive ? 'is-active' : ''} ${isComplete ? 'is-complete' : ''} ${!canAccess ? 'is-disabled' : ''} ${cannotAccess ? 'cannot_access_tab' : ''}`}
                                        onClick={() => canAccess && selectStep(stepNum)}
                                        disabled={!canAccess}
                                    >
                                        <span className='step_indicator' aria-hidden='true'>
                                            {isComplete ?
                                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                                    <path d="M10.8346 3.75L4.8763 9.70833L2.16797 7" stroke="#1E293B" stroke-width="1.625" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                                : <span className='step_dot' />}
                                        </span>
                                        <span className='step_content'>
                                            <span className='step_title'>
                                                {step.title} {step.optional && <small>(optional)</small>}
                                            </span>
                                            <span className='step_description'>{step.description}</span>
                                        </span>
                                        {isActive && <span className='step_arrow' aria-hidden='true'>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                <path d="M5.25 10.5L8.75 7L5.25 3.5" stroke="white" strokeOpacity="0.6" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </span>}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>

                </div>
            </div>

            {/* ──── Right Content Area ──── */}
            <div className="container_box--wrapper">
                <div className={(currentStatus === "onboard" || currentStatus === "rejected") ? 'onboarding_tab_content scroll_height_div_new_cls' : 'onboarding_tab_content'}>

                    {currentStatus === "onboard" &&
                        <div className="container_box--width w-100">
                            <Row>
                                <Col span={24}>
                                    <div className='profile_review_alert_div mb-0 mt-3'>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
                                            <path d="M18 5.625C15.33 5.625 12.7199 6.41676 10.4998 7.90016C8.27975 9.38356 6.54942 11.492 5.52763 13.9588C4.50585 16.4256 4.2385 19.14 4.7594 21.7587C5.28031 24.3775 6.56606 26.7829 8.45406 28.6709C10.3421 30.559 12.7475 31.8447 15.3663 32.3656C17.985 32.8865 20.6994 32.6192 23.1662 31.5974C25.633 30.5756 27.7414 28.8453 29.2248 26.6252C30.7082 24.4051 31.5 21.795 31.5 19.125C31.4959 15.5458 30.0723 12.1144 27.5414 9.58358C25.0106 7.05273 21.5792 5.62909 18 5.625ZM18 30.375C15.775 30.375 13.5999 29.7152 11.7498 28.479C9.89979 27.2429 8.45785 25.4859 7.60636 23.4302C6.75488 21.3745 6.53209 19.1125 6.96617 16.9302C7.40026 14.7479 8.47171 12.7434 10.0451 11.17C11.6184 9.59671 13.623 8.52525 15.8052 8.09117C17.9875 7.65708 20.2495 7.87987 22.3052 8.73136C24.3609 9.58284 26.1179 11.0248 27.354 12.8748C28.5902 14.7249 29.25 16.9 29.25 19.125C29.2467 22.1077 28.0603 24.9672 25.9513 27.0763C23.8422 29.1853 20.9827 30.3717 18 30.375ZM24.4209 12.7041C24.5255 12.8085 24.6085 12.9326 24.6651 13.0692C24.7218 13.2058 24.7509 13.3522 24.7509 13.5C24.7509 13.6478 24.7218 13.7942 24.6651 13.9308C24.6085 14.0674 24.5255 14.1915 24.4209 14.2959L18.7959 19.9209C18.6914 20.0255 18.5673 20.1084 18.4308 20.1649C18.2942 20.2215 18.1478 20.2506 18 20.2506C17.8522 20.2506 17.7058 20.2215 17.5692 20.1649C17.4327 20.1084 17.3086 20.0255 17.2041 19.9209C17.0995 19.8164 17.0166 19.6923 16.9601 19.5558C16.9035 19.4192 16.8744 19.2728 16.8744 19.125C16.8744 18.9772 16.9035 18.8308 16.9601 18.6942C17.0166 18.5577 17.0995 18.4336 17.2041 18.3291L22.8291 12.7041C22.9335 12.5995 23.0576 12.5165 23.1942 12.4599C23.3308 12.4033 23.4772 12.3741 23.625 12.3741C23.7728 12.3741 23.9192 12.4033 24.0558 12.4599C24.1924 12.5165 24.3165 12.5995 24.4209 12.7041ZM13.5 2.25C13.5 1.95163 13.6185 1.66548 13.8295 1.4545C14.0405 1.24353 14.3266 1.125 14.625 1.125H21.375C21.6734 1.125 21.9595 1.24353 22.1705 1.4545C22.3815 1.66548 22.5 1.95163 22.5 2.25C22.5 2.54837 22.3815 2.83452 22.1705 3.0455C21.9595 3.25647 21.6734 3.375 21.375 3.375H14.625C14.3266 3.375 14.0405 3.25647 13.8295 3.0455C13.6185 2.83452 13.5 2.54837 13.5 2.25Z" fill="#894B00" />
                                        </svg>
                                        <div>
                                            <h3>You’re onboarded — let’s keep the momentum going.</h3>
                                            <p>Add your inventory details now to help us review, configure, and activate your account faster.</p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    }
                    {currentStatus === "rejected" &&
                        <div className="container_box--width w-100">
                            <Row>
                                <Col span={24}>
                                    <div className='profile_review_alert_div --rejected mb-0 mt-3'>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
                                            <path d="M18 33C26.2843 33 33 26.2843 33 18C33 9.71573 26.2843 3 18 3C9.71573 3 3 9.71573 3 18C3 26.2843 9.71573 33 18 33Z" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M22.5 13.5L13.5 22.5" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M13.5 13.5L22.5 22.5" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <div>
                                            <h3>Your account is rejected</h3>
                                            <p>Thank you for your interest. We regret to inform you that your account registration has been rejected after a review of your submitted information. Unfortunately, it does not meet the required criteria at this time.</p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    }

                    {/* ═══════════════════════════════════════════════════════════
                        TAB 0: Basic Details
                    ═══════════════════════════════════════════════════════════ */}
                    {activeStep === 1 && (
                        <div className='tab_form basic_details_form'>
                            <div className="container_box--content  form_scrollable_div_d2">
                                <div className="container_box--width w-100">

                                    {/* Basic Details */}
                                    <Row gutter={0} justify="center">
                                        <Col span={5}>
                                        </Col>
                                        <Col span={14}>
                                            <div className="container_box--label-info">
                                                <label>Basic details</label>
                                            </div>
                                            <div className="container_box--form-container" ref={basicDetailsRef}>
                                                <Row gutter={16}>
                                                    <Col span={12}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Publisher Name *</label>
                                                            <Input className={nameIsRequiredError ? "form_control basic error" : "form_control basic"}
                                                                value={name} onBlur={onBlurName} onChange={onChangeName} autoComplete="off" disabled={isDisabled} />
                                                            <span className='error_content'>{nameIsRequiredError && <>This field is required</>}</span>
                                                        </div>
                                                    </Col>
                                                    <Col span={12}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Full name of primary POC *</label>
                                                            <Input className={pocIsRequiredError ? "form_control basic error" : "form_control basic"}
                                                                value={poc} onBlur={onBlurPoc} onChange={onChangePoc} autoComplete="off" disabled={isDisabled} />
                                                            <span className='error_content'>{pocIsRequiredError && <>This field is required</>}</span>
                                                        </div>
                                                    </Col>
                                                    <Col span={12}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Organisation Name *</label>
                                                            <Input className={companyNameIsRequiredError ? "form_control basic error" : "form_control basic"}
                                                                value={companyName} onBlur={onBlurCompanyName} onChange={onChangeCompanyName} autoComplete="off" disabled={isDisabled} />
                                                            <span className='error_content'>{companyNameIsRequiredError && <>This field is required</>}</span>
                                                        </div>
                                                    </Col>
                                                    <Col span={12}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Company website *</label>
                                                            <Input className={(companyWebsiteIsRequiredError || companyWebsiteUrlError) ? "form_control basic error" : "form_control basic"}
                                                                value={companyWebsite} onBlur={onBlurCompanyWebsite} onChange={onChangeCompanyWebsite} autoComplete="off" disabled={isDisabled} />
                                                            <span className='error_content'>
                                                                {companyWebsiteIsRequiredError && <>This field is required</>}
                                                                {companyWebsiteUrlError && <>Invalid Website Url</>}
                                                            </span>
                                                        </div>
                                                    </Col>
                                                    <Col span={12}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Email Address *</label>
                                                            <Input className="form_control basic" value={email} disabled />
                                                            <span className="error_content"> </span>
                                                        </div>
                                                    </Col>
                                                    <Col span={12}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">IM *</label>
                                                            <Space.Compact className='form_compact'>
                                                                <Select className='form_select' value={selectedIm} onChange={onChangeSelectedIm} options={constantData.IM_OPTIONS} disabled={isDisabled} />
                                                                <Input className={imIsRequiredError ? "form_control basic error" : "form_control basic"}
                                                                    value={im} onChange={onChangeIm} onBlur={onBlurIm} autoComplete="off" disabled={isDisabled} />
                                                            </Space.Compact>
                                                            <span className="error_content">{imIsRequiredError && <>This field is required</>}</span>
                                                        </div>
                                                    </Col>
                                                    <Col span={12}>
                                                        <div className="form_content_block--group mb-0">
                                                            <label className="form_label">Headquarter Country</label>
                                                            <Select showSearch placeholder='Select' optionFilterProp="label"
                                                                className="form_select basic showing_placeholder_text"
                                                                value={country || undefined}
                                                                onChange={(event) => { setCountry(event); }}
                                                                options={countryOptions} />
                                                        </div>
                                                    </Col>
                                                    <Col span={12}>
                                                        <div className="form_content_block--group mb-0">
                                                            <label className="form_label">LinkedIn</label>
                                                            <Input className={companyLinkedinUrlError ? "form_control basic error" : "form_control basic"}
                                                                placeholder='https://' value={companyLinkedin} onBlur={onBlurCompanyLinkedin} onChange={onChangeCompanyLinkedin} autoComplete="off" disabled={isDisabled} />
                                                            <span className="error_content">{companyLinkedinUrlError && <>Invalid LinkedIn Url</>}</span>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Col>
                                        <Col span={5}>
                                        </Col>
                                    </Row>

                                    {/* Define Yourself */}
                                    <Row gutter={0}>
                                        <Col span={5}>
                                        </Col>
                                        <Col span={14}>
                                            <div className="container_box--label-info">
                                                <label>Define Yourself *</label>
                                            </div>
                                            <div className="container_box--form-container" ref={defineYourselfRef}>
                                                <Row gutter={16}>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group m-0">
                                                            <Checkbox.Group className="form_checkbox equal_space" value={defineYourself} onChange={onChangeDefineYourself} disabled={isDisabled}>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Direct Publisher">Direct Publisher</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Aggregator / Reseller">Aggregator / Reseller</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Network with owned & operated apps/sites">Network with owned & operated apps/sites</Checkbox>
                                                            </Checkbox.Group>
                                                            <span className="error_content">{defineYourselfIsRequiredError && <>This field is required</>}</span>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Col>
                                        <Col span={5}>
                                        </Col>
                                    </Row>

                                    {/* MCM */}
                                    <Row gutter={0}>
                                        <Col span={5}>
                                        </Col>
                                        <Col span={14}>
                                            <div className="container_box--label-info">
                                                <label>Do you accept MCM (Multiple Customer Management) invitation? *</label>
                                            </div>
                                            <div className="container_box--form-container" ref={mcmRef}>
                                                <Row gutter={16}>
                                                    <Col span={24}>
                                                        <div className={isMcm ? "form_content_block--group" : "form_content_block--group mb-0"}>
                                                            <Radio.Group className="form_radio equal_space" onChange={onChangeIsMcm} value={isMcm} disabled={isDisabled}>
                                                                <Radio className='radio_btn w-auto' value={true}>Yes</Radio>
                                                                <Radio className='radio_btn w-auto' value={false}>No</Radio>
                                                            </Radio.Group>
                                                            <span className="error_content">{isMcmIsRequiredError && <>This field is required</>}</span>
                                                        </div>
                                                    </Col>
                                                    {isMcm && <>
                                                        <Col span={12}>
                                                            <div className="form_content_block--group mb-0">
                                                                <label className="form_label">Google Ad Manager Email ID *</label>
                                                                <Input className={(gamEmailIsRequiredError || gamEmailUrlError) ? 'form_control basic error' : 'form_control basic'}
                                                                    autoComplete="off" placeholder="example@gmail.com" value={gamEmail} onBlur={onBlurGamEmail} onChange={onChangeGamEmail} disabled={isDisabled} />
                                                                {gamEmailIsRequiredError && <span className="error_content">This field is required</span>}
                                                                {gamEmailUrlError && <span className="error_content">Invalid Email Address</span>}
                                                            </div>
                                                        </Col>
                                                        <Col span={12}>
                                                            <div className="form_content_block--group mb-0">
                                                                <label className="form_label">GAM Network Code *</label>
                                                                <Input className={gamNetworkIdIsRequiredError ? "form_control basic error" : "form_control basic"}
                                                                    value={gamNetworkId} onBlur={onBlurGamNetworkId} onChange={onChangeGamNetworkId} autoComplete="off" disabled={isDisabled} />
                                                                {gamNetworkIdIsRequiredError && <span className="error_content">This field is required</span>}
                                                            </div>
                                                        </Col>
                                                    </>}
                                                </Row>
                                            </div>
                                        </Col>
                                        <Col span={5}>
                                        </Col>
                                    </Row>

                                    {/* What kind of inventory */}
                                    <Row gutter={0}>
                                        <Col span={5}>
                                        </Col>
                                        <Col span={14}>
                                            <div className="container_box--label-info">
                                                <label>What kind of inventory do you want to monetize with us? *</label>
                                            </div>
                                            <div className="container_box--form-container" ref={inventoryMonetizeRef}>
                                                <Row gutter={16}>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group mb-0">
                                                            <Checkbox.Group className="form_checkbox equal_space" value={inventoryMonetize} onChange={onChangeInventoryMonetize} disabled={isDisabled}>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="CTV / FAST Apps">CTV / FAST Apps</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Mobile App (Android / iOS)">Mobile App (Android / iOS)</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Website">Website</Checkbox>
                                                                <Checkbox className={inventoryMonetize.includes("Others") ? "checkbox_btn w-100 checkbox_with_input_design" : "checkbox_btn withoutchecked w-100 checkbox_with_input_design"} value="Others">
                                                                    Others
                                                                    <Input className='form_control basic' placeholder='Enter here' autoComplete="off"
                                                                        disabled={(!inventoryMonetize.includes("Others") || isDisabled)}
                                                                        value={inventoryMonetize.includes("Others") ? inventoryMonetizeOther : ""}
                                                                        onChange={(e) => {
                                                                            setInventoryMonetizeOther(e.target.value);
                                                                            if (e.target.value.trim().length > 0) setInventoryMonetizeOtherIsRequiredError(false);
                                                                        }} />
                                                                </Checkbox>
                                                                {inventoryMonetize.includes("Others") && inventoryMonetizeOtherIsRequiredError && (
                                                                    <span className="error_content">Please provide value for others</span>
                                                                )}
                                                            </Checkbox.Group>
                                                            {inventoryMonetizeIsRequiredError && <span className="error_content">This field is required</span>}
                                                        </div>
                                                    </Col>
                                                    {inventoryMonetize.includes("Website") && <>
                                                        <Col span={24}><hr className='divider_line' /></Col>
                                                        <Col span={24}>
                                                            <div className="form_content_block--group">
                                                                <label className="form_label">Do you support User Syncing on websites?</label>
                                                                <Radio.Group className="form_radio equal_space" value={inventoryMonetizeWebsiteUserSync} onChange={onChangeInventoryMonetizeWebsiteUserSync} disabled={isDisabled}>
                                                                    <Radio className='radio_btn w-auto' value={true}>Yes</Radio>
                                                                    <Radio className='radio_btn w-auto' value={false}>No</Radio>
                                                                </Radio.Group>
                                                                {inventoryMonetizeWebsiteUserSyncIsRequiredError && <span className="error_content">This field is required</span>}
                                                            </div>
                                                        </Col>
                                                        <Col span={24}>
                                                            <div className="form_content_block--group mb-0">
                                                                <label className="form_label">Do you allow Outstream video (IBV) over banner placements? (Website)</label>
                                                                <Radio.Group className="form_radio equal_space" value={inventoryMonetizeWebsiteBanner} onChange={onChangeInventoryMonetizeWebsiteBanner} disabled={isDisabled}>
                                                                    <Radio className='radio_btn w-auto' value={true}>Yes</Radio>
                                                                    <Radio className='radio_btn w-auto' value={false}>No</Radio>
                                                                </Radio.Group>
                                                                {inventoryMonetizeWebsiteBannerIsRequiredError && <span className="error_content">This field is required</span>}
                                                            </div>
                                                        </Col>
                                                    </>}
                                                </Row>
                                            </div>
                                        </Col>
                                        <Col span={5}>
                                        </Col>
                                    </Row>

                                    {/* Media Kit - Optional */}
                                    <Row gutter={0}>
                                        <Col span={5}></Col>
                                        <Col span={14}>
                                            <div className="container_box--label-info">
                                                <label>Media Kit</label>
                                            </div>
                                            <div className="container_box--form-container mb-0" ref={importUrlRef}
                                                style={{ paddingBottom: ((mediakit?.length ?? 0) + (mediakit2?.length ?? 0)) === 0 || ((mediakit?.length ?? 0) + (mediakit2?.length ?? 0)) === 1 ? "0px" : undefined }}>
                                                {(mediakit?.length + mediakit2?.length) === 0 &&
                                                    <Row gutter={16}>
                                                        <Col span={24} className='file_uploaded_view_cls'>
                                                            <div className="form_content_block--group mb-0">
                                                                <label htmlFor='file000_step3' className='ant_upload_drag --updated_design_cls'>
                                                                    <div className='d-flex align-items-center justify-content-start' style={{ gap: '1.2rem' }}>
                                                                        <p className="upload_drag_icon mx-0"><img src={uploadIcon} alt='USP' /></p>
                                                                        <div className='text-start'>
                                                                            <p className="ant_upload_text mt-0"><b className="primary">Click to upload</b></p>
                                                                            <p className="text-start ant_upload_hint">(max. 100 MB)</p>
                                                                        </div>
                                                                    </div>
                                                                </label>
                                                                <input className='form_upload d-none' type="file" name="file000_step3" id="file000_step3" multiple onChange={onChangeMediakit} disabled={isDisabled} />
                                                            </div>
                                                        </Col>
                                                        <Col span={24} className="">
                                                            <div className="or-divider --horizontal_divider"><span>or</span></div>
                                                        </Col>
                                                        <Col span={24}>
                                                            <div className="form_content_block--group">
                                                                <label className="form_label">Import from URL</label>
                                                                <Input className={importUrlError ? "form_control basic error" : "form_control basic"}
                                                                    placeholder='Paste your media kit URL' value={importUrl} onChange={onChangeImportUrl} onBlur={onBlurImportUrl} disabled={isDisabled} />
                                                                <span className="error_content">{importUrlError && <>Invalid Url</>}</span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                }
                                                {(mediakit?.length + mediakit2?.length) !== 0 &&
                                                    <Row gutter={16}>
                                                        <Col span={24} className='file_uploaded_view_cls'>
                                                            <div className='new_file_upload_design_cls' style={{ paddingBottom: "0px" }}>
                                                                <div className='d-flex align-items-start justify-content-between'>
                                                                    <div style={{ width: "93%" }}>
                                                                        {mediakit.map((media, index) => (
                                                                            <div key={index} className="ant_upload_list--content border-0 padding-0">
                                                                                <div className='d-flex align-items-center justify-content-between w-100'>
                                                                                    <div className='d-flex align-items-center' style={{ width: "160px" }}>
                                                                                        <img src={FileIcon} alt="USP" />
                                                                                        <div className="ant_upload_list--info">
                                                                                            <span className="ant_upload_list--info-name new_design_name ellipsis-2_text">
                                                                                                <Tooltip title={media.filename}>{media.filename}</Tooltip>
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className='d-flex'>
                                                                                        <button type="link" className="ant_upload_list--delete"
                                                                                            onClick={() => { let temp = [...mediakit]; temp.splice(index, 1); setMediakit(temp); }} disabled={isDisabled}>
                                                                                            <Tooltip title="Delete"><img src={deleteIcon} alt="USP" /></Tooltip>
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                        {mediakit2?.map((media, index) => (
                                                                            <div key={index} className="ant_upload_list--content border-0 padding-0">
                                                                                <div className='d-flex align-items-start justify-content-between w-100'>
                                                                                    <div className='d-flex align-items-center' style={{ width: "160px" }}>
                                                                                        <img src={FileIcon} alt="USP" />
                                                                                        <div className="ant_upload_list--info">
                                                                                            <span className="ant_upload_list--info-name new_design_name ellipsis-2_text">
                                                                                                <Tooltip title={media.fileName}>{media.fileName}</Tooltip>
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className='d-flex'>
                                                                                        {media.hasOwnProperty('url') &&
                                                                                            <button type="link" className="ant_upload_list--delete" onClick={() => fetchAndDownloadFilesMediaKit(media)}>
                                                                                                <Tooltip title="Download"><img src={DownloadOrangeIcon} alt="USP" /></Tooltip>
                                                                                            </button>
                                                                                        }
                                                                                        <button type="link" className="ant_upload_list--delete"
                                                                                            onClick={() => { let temp = [...mediakit2]; temp.splice(index, 1); setMediakit2(temp); }} disabled={isDisabled}>
                                                                                            <Tooltip title="Delete"><img src={deleteIcon} alt="USP" /></Tooltip>
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    <Tooltip title="Upload More">
                                                                        <button type="button" className="upload_btn">
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                                <path d="M10.3637 10.3645L7.99924 8L5.63477 10.3645" stroke="#7434DB" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M8 8V13.3201" stroke="#7434DB" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M12.9591 11.7773C13.5356 11.4629 13.9911 10.9656 14.2536 10.3637C14.516 9.76176 14.5706 9.08958 14.4086 8.45321C14.2467 7.81685 13.8774 7.25255 13.3591 6.84937C12.8408 6.44618 12.203 6.22709 11.5463 6.22665H10.8015C10.6226 5.5346 10.2891 4.89211 9.82612 4.34748C9.36315 3.80286 8.78274 3.37028 8.12852 3.08226C7.4743 2.79425 6.7633 2.65828 6.04897 2.6846C5.33465 2.71092 4.63558 2.89884 4.00433 3.23422C3.37308 3.5696 2.82607 4.04371 2.40444 4.62093C1.9828 5.19814 1.6975 5.86343 1.56999 6.56678C1.44248 7.27013 1.47607 7.99323 1.66825 8.68172C1.86043 9.37022 2.20619 10.0062 2.67953 10.5418" stroke="#7434DB" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M10.3637 10.3645L7.99924 8L5.63477 10.3645" stroke="#7434DB" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                                                            </svg>
                                                                            <input className='upload_btn' type="file" name="file000_step3" id="file000_step3" multiple onChange={onChangeMediakit} disabled={isDisabled} />
                                                                        </button>
                                                                    </Tooltip>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                        <Col span={24}>
                                                            <div className="or-divider --horizontal_divider"><span>or</span></div>
                                                        </Col>
                                                        <Col span={24}>
                                                            <div className="form_content_block--group">
                                                                <label className="form_label">Import from URL</label>
                                                                <Input className={importUrlError ? "form_control basic error" : "form_control basic"}
                                                                    placeholder='Paste your media kit URL' value={importUrl} onChange={onChangeImportUrl} onBlur={onBlurImportUrl} disabled={isDisabled} />
                                                                <span className="error_content">{importUrlError && <>Invalid Url</>}</span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                }
                                            </div>
                                        </Col>
                                        <Col span={5}></Col>
                                    </Row>

                                </div>

                                {/* Save button */}
                                {/* <div style={{ marginBottom: "8rem" }}></div> */}
                                {<>
                                    {/* <div className="hr_custom mb_20"></div> */}
                                    <div className="btn_group justify-content-end gap_8 pb_50 edit_view_fixedbtn_d2">
                                        {buttonValue ? (
                                            <button className='btn_primary btn loader w_120_d2 btn btn_primary w_fit_content' style={{ cursor: "default" }}>
                                                <span>{canFreelyNavigate ? 'Save' : 'Save & Next'}</span><img src={loaderIcon} alt='USP' className='loader_icon' />
                                            </button>
                                        ) : (
                                            <button className='w_120_d2 btn btn_primary w_fit_content' onClick={handleSave}>
                                                <span>{canFreelyNavigate ? 'Save' : 'Save & Next'}</span><img src={loaderIcon} alt='USP' className='loader_icon' />
                                            </button>
                                        )}
                                    </div>
                                </>}
                            </div>
                        </div>
                    )}


                    {/* ═══════════════════════════════════════════════════════════
                        TAB 1: Ad Formats
                    ═══════════════════════════════════════════════════════════ */}
                    {activeStep === 2 && (
                        <div className='tab_form ad_formats_form'>
                            <div className="container_box--content  form_scrollable_div_d2">
                                <div className="container_box--width w-100">

                                    {/* What ad formats */}
                                    <Row gutter={0}>
                                        <Col span={5}></Col>
                                        <Col span={14}>
                                            <div className="container_box--label-info">
                                                <label>What ad formats do you want to monetize? *</label>
                                            </div>
                                            <div className="container_box--form-container" ref={adFormatMonetizeRef}>
                                                <Row gutter={16}>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group mb-0">
                                                            <Checkbox.Group className="form_checkbox equal_space" value={adFormatMonetize} onChange={onChangeAdFormatMonetize} disabled={isDisabled}>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Instream Video">Instream Video</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Outstream / In-Article Video">Outstream / In-Article Video</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Rewarded Video">Rewarded Video</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Banner (Display)">Banner (Display)</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Native">Native</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Pause Ads">Pause Ads</Checkbox>
                                                                <Checkbox className={adFormatMonetize.includes("Others") ? "checkbox_btn w-100 checkbox_with_input_design" : "checkbox_btn withoutchecked w-100 checkbox_with_input_design"} value="Others">
                                                                    Others
                                                                    <Input className='form_control basic' placeholder='Enter here' autoComplete="off"
                                                                        disabled={(!adFormatMonetize.includes("Others") || isDisabled)}
                                                                        value={adFormatMonetize.includes("Others") ? adFormatMonetizeOther : ""}
                                                                        onChange={(e) => {
                                                                            setAdFormatMonetizeOther(e.target.value);
                                                                            if (e.target.value.trim().length > 0) setAdFormatMonetizeOtherIsRequiredError(false);
                                                                        }} />
                                                                </Checkbox>
                                                                {adFormatMonetize.includes("Others") && adFormatMonetizeOtherIsRequiredError && (
                                                                    <span className="error_content">Please provide value for others</span>
                                                                )}
                                                            </Checkbox.Group>
                                                            {adFormatMonetizeIsRequiredError && <span className="error_content">This field is required</span>}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Col>
                                        <Col span={5}></Col>
                                    </Row>

                                    {/* Preferred Integration Method */}
                                    <Row gutter={0}>
                                        <Col span={5}></Col>
                                        <Col span={14}>
                                            <div className="container_box--label-info"><label>Preferred Integration Method *</label></div>
                                            <div className="container_box--form-container" ref={integrationTypeRef}>
                                                <Row gutter={16}>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group mb-0">
                                                            <Checkbox.Group className="form_checkbox equal_space" value={integrationType} onChange={onChangeIntegrationType} disabled={isDisabled}>
                                                                <Checkbox className="checkbox_btn w-100" value="Prebid">Prebid</Checkbox>
                                                                {integrationType.includes("Prebid") && <>
                                                                    <Col span={24}>
                                                                        <div className="form_content_block--group hide_caret_after_select_value mb-0 mt-3">
                                                                            <label className="form_label">If you support header bidding, please specify the solution used. (e.g., Prebid.js, Amazon, PubMatic OpenWrap, etc) *</label>
                                                                            <TextArea className={prebidQuestionIsRequiredError ? 'form_control error basic form_control_h --80 textarea_paddingY_d2' : 'form_control basic form_control_h --80 textarea_paddingY_d2'}
                                                                                autoComplete="off" autoSize={{ minRows: 3, maxRows: 5 }}
                                                                                value={prebidQuestion} onChange={onChangePrebidQuestion} onBlur={onBlurPrebidQuestion} disabled={isDisabled} />
                                                                            <span className="error_content">{prebidQuestionIsRequiredError && <>This field is required</>}</span>
                                                                        </div>
                                                                    </Col>
                                                                    <Col span={24}><hr className="divider_line" /></Col>
                                                                </>}
                                                                <Checkbox className="checkbox_btn w-100" value="ORTB">ORTB</Checkbox>
                                                                {integrationType.includes("ORTB") && <>
                                                                    <Col span={24}>
                                                                        <div className="form_content_block--group mb-0 mt-3">
                                                                            <label className="form_label">If your setup supports OpenRTB, please specify the version. (e.g., 2.3, 2.5, 2.6, etc) *</label>
                                                                            <Input className={ortbQuestionIsRequiredError ? 'form_control basic error' : 'form_control basic'} placeholder='Enter version' autoComplete="off"
                                                                                value={ortbQuestion} onChange={onChangeOrtbQuestion} onBlur={onBlurOrtbQuestion} disabled={isDisabled} />
                                                                            <span className="error_content">{ortbQuestionIsRequiredError && <>This field is required</>}</span>
                                                                        </div>
                                                                    </Col>
                                                                    <Col span={24}>
                                                                        <div className="form_content_block--group mb-0">
                                                                            <label className="form_label">Select the impression counting method you support. *</label>
                                                                            <Checkbox.Group className="form_checkbox equal_space" value={ortbType} onChange={onChangeOrtbType} disabled={isDisabled}>
                                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="ADM">ADM</Checkbox>
                                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="NURL">NURL</Checkbox>
                                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="BURL">BURL</Checkbox>
                                                                            </Checkbox.Group>
                                                                            <span className='error_content'>{ortbTypeIsRequiredError && <>Minimum 1 selection is required</>}</span>
                                                                        </div>
                                                                    </Col>
                                                                    <Col span={24}><hr className="divider_line" /></Col>
                                                                </>}
                                                                <Checkbox className="checkbox_btn w-100" value="VAST">VAST</Checkbox>
                                                                {integrationType.includes("VAST") && <>
                                                                    <Col span={24}>
                                                                        <div className="form_content_block--group mb-0 mt-3">
                                                                            <label className="form_label">Select the supported VAST Version *</label>
                                                                            <Checkbox.Group className="form_checkbox equal_space" value={vastType} onChange={onChangeVastType} disabled={isDisabled}>
                                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="VAST VERSION 2.0">VAST VERSION 2.0</Checkbox>
                                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="VAST VERSION 3.0">VAST VERSION 3.0</Checkbox>
                                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="VAST VERSION 4.0">VAST VERSION 4.0</Checkbox>
                                                                            </Checkbox.Group>
                                                                        </div>
                                                                        <span className="error_content">{vastTypeIsRequiredError && <>Minimum 1 selection is required</>}</span>
                                                                    </Col>
                                                                </>}
                                                            </Checkbox.Group>
                                                            <span className='error_content'>{integrationTypeIsRequiredError && <>This field is required</>}</span>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Col>
                                        <Col span={5}></Col>
                                    </Row>

                                    {/* Integration & Protocol Support */}
                                    <Row gutter={0}>
                                        <Col span={5}>
                                        </Col>
                                        <Col span={14}>
                                            <div className="container_box--label-info"><label>Integration & Protocol Support</label></div>
                                            <div className="container_box--form-container mb-0" ref={integrationProtocolDetailsRef}>
                                                <Row gutter={16}>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Which Player/SDK Used for Video Inventory?</label>
                                                            <Input className='form_control basic' autoComplete="off" value={integrationProtocolPlayer} onBlur={onBlurIntegrationProtocolPlayer} onChange={onChangeIntegrationProtocolPlayer} disabled={isDisabled} />
                                                        </div>
                                                    </Col>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">List the MIME types your ad placements support. (e.g., video/mp4, application/javascript, text/html) *</label>
                                                            <Input className={integrationProtocolMimeIsRequiredError ? "form_control basic error" : "form_control basic"}
                                                                value={integrationProtocolMime} onBlur={onBlurIntegrationProtocolMime} onChange={onChangeIntegrationProtocolMime} autoComplete="off" disabled={isDisabled} />
                                                            <span className='error_content'>{integrationProtocolMimeIsRequiredError && <>This field is required</>}</span>
                                                        </div>
                                                    </Col>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Please specify your maximum timeout (Tmax) in milliseconds.</label>
                                                            <Input className='form_control basic' autoComplete="off" value={integrationProtocolTimeout} onBlur={onBlurIntegrationProtocolTimeout} onChange={onChangeIntegrationProtocolTimeout} disabled={isDisabled} />
                                                        </div>
                                                    </Col>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Please specify where your ad servers are hosted. (e.g., AWS US-East, GCP Europe, etc)</label>
                                                            <Input className='form_control basic' autoComplete="off" value={integrationProtocolAdserver} onBlur={onBlurIntegrationProtocolAdserver} onChange={onChangeIntegrationProtocolAdserver} disabled={isDisabled} />
                                                        </div>
                                                    </Col>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Do you support multiple impressions in a single request</label>
                                                            <Radio.Group className="form_radio equal_space" disabled={isDisabled} onChange={onChangeIntegrationProtocolImpression} value={integrationProtocolImpression}>
                                                                <Radio className='radio_btn w-auto' value={true}>Yes</Radio>
                                                                <Radio className='radio_btn w-auto' value={false}>No</Radio>
                                                            </Radio.Group>
                                                        </div>
                                                    </Col>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Do your bid requests include content-level parameters such as channel, genre, language, or content rating?</label>
                                                            <Input className='form_control basic' autoComplete="off" value={integrationProtocolBidrequest} onBlur={onBlurIntegrationProtocolBidrequest} onChange={onChangeIntegrationProtocolBidrequest} disabled={isDisabled} />
                                                        </div>
                                                    </Col>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Can you pass buyer uid and uid in requests?</label>
                                                            <Radio.Group className="form_radio equal_space" disabled={isDisabled} onChange={onChangeIntegrationProtocolUid} value={integrationProtocolUid}>
                                                                <Radio className='radio_btn w-auto' value={true}>Yes</Radio>
                                                                <Radio className='radio_btn w-auto' value={false}>No</Radio>
                                                            </Radio.Group>
                                                        </div>
                                                    </Col>
                                                    <Col span={24}>
                                                        <div className={integrationProtocolRichMedia ? "form_content_block--group" : "form_content_block--group mb-0"}>
                                                            <label className="form_label">Do you support Rich Media creatives? *</label>
                                                            <Radio.Group className="form_radio equal_space" onChange={onChangeIntegrationProtocolRichMedia} value={integrationProtocolRichMedia} disabled={isDisabled}>
                                                                <Radio className='radio_btn w-auto' value={true}>Yes</Radio>
                                                                <Radio className='radio_btn w-auto' value={false}>No</Radio>
                                                            </Radio.Group>
                                                            <span className="error_content">{integrationProtocolRichMediaIsRequiredError && <>This field is required</>}</span>
                                                        </div>
                                                    </Col>
                                                    {integrationProtocolRichMedia &&
                                                        <Col span={24}>
                                                            <div className="form_content_block--group mb-0">
                                                                <label className="form_label">Select supported Rich Media creatives</label>
                                                                <Checkbox.Group className="form_checkbox equal_space" value={integrationProtocolRichMediaCreative} onChange={onChangeIntegrationProtocolRichMediaCreative} disabled={isDisabled}>
                                                                    <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="MRAID">MRAID</Checkbox>
                                                                    <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="HTML">HTML</Checkbox>
                                                                    <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="Expandable">Expandable</Checkbox>
                                                                    <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="JS">JS</Checkbox>
                                                                </Checkbox.Group>
                                                            </div>
                                                        </Col>
                                                    }
                                                </Row>
                                            </div>
                                        </Col>
                                        <Col span={5}>
                                        </Col>
                                    </Row>

                                </div>

                                {/* Save button */}
                                {/* <div style={{ marginBottom: "8rem" }}></div> */}
                                {<>
                                    {/* <div className="hr_custom mb_20"></div> */}
                                    <div className="btn_group justify-content-end gap_8 pb_50 edit_view_fixedbtn_d2">
                                        {buttonValue ? (
                                            <button className='btn_primary btn loader w_120_d2 btn btn_primary w_fit_content' style={{ cursor: "default" }}>
                                                <span>{canFreelyNavigate ? 'Save' : 'Save & Next'}</span><img src={loaderIcon} alt='USP' className='loader_icon' />
                                            </button>
                                        ) : (
                                            <button className='w_120_d2 btn btn_primary w_fit_content' onClick={handleSave}>
                                                <span>{canFreelyNavigate ? 'Save' : 'Save & Next'}</span><img src={loaderIcon} alt='USP' className='loader_icon' />
                                            </button>
                                        )}
                                    </div>
                                </>}
                            </div>
                        </div>
                    )}


                    {/* ═══════════════════════════════════════════════════════════
                        TAB 2: Compliance & Safety
                    ═══════════════════════════════════════════════════════════ */}
                    {activeStep === 3 && (
                        <div className='tab_form compliance_safety_form'>
                            <div className="container_box--content  form_scrollable_div_d2">
                                <div className="container_box--width w-100">

                                    {/* Regulation & Brand Safety */}
                                    <Row gutter={0}>
                                        <Col span={5}>
                                        </Col>
                                        <Col span={14}>
                                            <div className="container_box--label-info"><label>Regulation & Brand Safety</label></div>
                                            <div className="container_box--form-container" ref={regulationDetailsRef}>
                                                <Row gutter={16}>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Which anti-fraud tools do you use? (e.g., DoubleVerify, Human, or other providers) *</label>
                                                            <Input className={regulationToolIsRequiredError ? "form_control basic error" : "form_control basic"}
                                                                value={regulationTool} onBlur={onBlurRegulationTool} onChange={onChangeRegulationTool} disabled={isDisabled} autoComplete="off" />
                                                            <span className='error_content'>{regulationToolIsRequiredError && <>This field is required</>}</span>
                                                        </div>
                                                    </Col>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group">
                                                            <label className="form_label">Which adserver do you use? (GAM, SpringServe, Publica, etc.) *</label>
                                                            <Input className={regulationAdserverIsRequiredError ? "form_control basic error" : "form_control basic"}
                                                                value={regulationAdserver} onBlur={onBlurRegulationAdserver} onChange={onChangeRegulationAdserver} disabled={isDisabled} autoComplete="off" />
                                                            <span className='error_content'>{regulationAdserverIsRequiredError && <>This field is required</>}</span>
                                                        </div>
                                                    </Col>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group mb-0">
                                                            <label className="form_label">Which regulatory parameters you support ? *</label>
                                                            <Checkbox.Group className="form_checkbox equal_space" value={regulationParameter} onChange={onChangeRegulationParameter} disabled={isDisabled}>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="GDPR">GDPR</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="COPPA">COPPA</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="US Privacy">US Privacy</Checkbox>
                                                                <Checkbox className="checkbox_btn --flex_grow_cls integration_checkbox_width" value="TCF">TCF</Checkbox>
                                                                <Checkbox className={regulationParameter.includes("Others") ? "checkbox_btn w-100 checkbox_with_input_design" : "checkbox_btn withoutchecked w-100 checkbox_with_input_design"} value="Others">
                                                                    Others
                                                                    <Input className='form_control basic' placeholder='Enter here' autoComplete="off"
                                                                        disabled={(!regulationParameter.includes("Others") || isDisabled)}
                                                                        value={regulationParameter.includes("Others") ? regulationParameterOther : ""}
                                                                        onChange={(e) => {
                                                                            setRegulationParameterOther(e.target.value);
                                                                            if (e.target.value.trim().length > 0) setRegulationParameterOtherIsRequiredError(false);
                                                                        }} />
                                                                </Checkbox>
                                                                {regulationParameter.includes("Others") && regulationParameterOtherIsRequiredError && (
                                                                    <span className="error_content">Please provide value for others</span>
                                                                )}
                                                            </Checkbox.Group>
                                                            {regulationParameterIsRequiredError && <span className="error_content">This field is required</span>}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Col>
                                        <Col span={5}>
                                        </Col>
                                    </Row>

                                    {/* Sensitive Categories */}
                                    <Row gutter={0}>
                                        <Col span={5}>
                                        </Col>
                                        <Col span={14}>
                                            <div className="container_box--label-info"><label>Sensitive categories you would like us to block on your supply.</label></div>
                                            <div className="container_box--form-container mb-0" ref={sensitiveDetailsRef}>
                                                <Row gutter={16}>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group mb-0">
                                                            <div className='alert_yellow_bg_div'>
                                                                <p>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                        <g clip-path="url(#clip0_16680_31874)">
                                                                            <path d="M5.2387 1.33325H10.7587L14.6654 5.23992V10.7599L10.7587 14.6666H5.2387L1.33203 10.7599V5.23992L5.2387 1.33325Z" stroke="#894B00" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                                                                            <path d="M8 10.6667H8.00667" stroke="#894B00" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                                                                            <path d="M8 5.33325V7.99992" stroke="#894B00" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                                                                        </g>
                                                                        <defs>
                                                                            <clipPath id="clip0_16680_31874">
                                                                                <rect width="16" height="16" fill="white" />
                                                                            </clipPath>
                                                                        </defs>
                                                                    </svg>
                                                                    If no categories selected, Helix would treat the inventory as category insensitive & would allow all advertisers to run through.
                                                                </p>
                                                            </div>
                                                            <div className="block-category block_category_adjustment --table_width_css onboarding-sensitive-category-table">
                                                                <div className="form_content_block--group mb-0">
                                                                    <div className="d_flex justify_content_between mb_10 align-items-center">
                                                                        <Input type='text' className='form_control basic md search_input w_table_search' placeholder="Search"
                                                                            value={search} onChange={e => setSearch(e.target.value)} />
                                                                        <div className='primary_checkbox mb_10'>
                                                                            <Checkbox checked={blockAllChecked} onChange={handleBlockAll} disabled={isDisabled}>
                                                                                <span className="form_label">Block all sensitive categories</span>
                                                                            </Checkbox>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="table-body-scroll without_hover_tr">
                                                                    <table className="category-table --category_table_newspacing">
                                                                        <tbody className="scroll-body">
                                                                            {DATA.length > 0 ? (
                                                                                visibleNodes.length === 0 ? (
                                                                                    <tr><td colSpan={2} style={{ textAlign: "center", height: "20rem", width: "72rem" }}><div className="no-data">No data found</div></td></tr>
                                                                                ) : (
                                                                                    visibleNodes.map(node => (
                                                                                        <CategoryRows
                                                                                            key={node.id}
                                                                                            node={node}
                                                                                            selected={selected}
                                                                                            setSelected={setSelected}
                                                                                            searchTerm={searchTerm.toLowerCase()}
                                                                                            isDisabled={isDisabled}
                                                                                            sensitive={sensitive}
                                                                                            setSensitive={setSensitive}
                                                                                            attemptedIds={sensitiveAttemptedIds}
                                                                                            setSensitiveAttemptedIds={setSensitiveAttemptedIds}
                                                                                        />
                                                                                    ))
                                                                                )
                                                                            ) : (
                                                                                <tr><td colSpan={2} style={{ textAlign: "center", height: "20rem", width: "72rem" }}><div className="no-data">No data found</div></td></tr>
                                                                            )}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Col>
                                        <Col span={5}>
                                        </Col>
                                    </Row>

                                </div>

                                {/* Save button */}
                                {/* <div style={{ marginBottom: "8rem" }}></div> */}
                                {<>
                                    {/* <div className="hr_custom mb_20"></div> */}
                                    <div className="btn_group justify-content-end gap_8 pb_50 edit_view_fixedbtn_d2">
                                        {buttonValue ? (
                                            <button className='btn_primary btn loader w_120_d2 btn btn_primary w_fit_content' style={{ cursor: "default" }}>
                                                <span>{canFreelyNavigate ? 'Save' : 'Save & Next'}</span><img src={loaderIcon} alt='USP' className='loader_icon' />
                                            </button>
                                        ) : (
                                            <button className='w_120_d2 btn btn_primary w_fit_content' onClick={handleSave}>
                                                <span>{canFreelyNavigate ? 'Save' : 'Save & Next'}</span><img src={loaderIcon} alt='USP' className='loader_icon' />
                                            </button>
                                        )}
                                    </div>
                                </>}
                            </div>
                        </div>
                    )}


                    {/* ═══════════════════════════════════════════════════════════
                        TAB 3: Inventory
                    ═══════════════════════════════════════════════════════════ */}
                    {activeStep === 4 && (
                        <div className='tab_form inventory_form'>
                            <div className="container_box--content  form_scrollable_div_d2">
                                <div className="container_box--width w-100">

                                    {/* Inventory - Link only */}
                                    <Row gutter={0}>
                                        <Col span={5}>
                                        </Col>
                                        <Col span={14}>
                                            <div className="container_box--label-info"><label>Inventory</label></div>
                                            <div className="container_box--form-container">
                                                <Row gutter={16}>
                                                    <Col span={24}>
                                                        <div className="form_content_block--group mb-0">
                                                            <p className="form_label bordered_link_text">
                                                                {/* To manage your inventory, please visit the */}
                                                                {' '}
                                                                {isRegisterFlow ?
                                                                    <Link to="/signup-inventory" className="text_primary" style={{ textDecoration: 'underline' }}>
                                                                        Upload your Inventory
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                            <path d="M5.83203 14.1666L14.1654 5.83325" stroke="#7434DB" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                                                                            <path d="M5.83203 5.83325H14.1654V14.1666" stroke="#7434DB" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                                                                        </svg>
                                                                    </Link> :
                                                                    <Link to="/inventory" className="text_primary" style={{ textDecoration: 'underline' }}>
                                                                        Upload your Inventory
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                            <path d="M5.83203 14.1666L14.1654 5.83325" stroke="#7434DB" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                                                                            <path d="M5.83203 5.83325H14.1654V14.1666" stroke="#7434DB" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                                                                        </svg>
                                                                    </Link>
                                                                }
                                                            </p>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Col>
                                        <Col span={5}>
                                        </Col>
                                    </Row>

                                </div>

                                {/* Finish button */}
                                {/* {<>
                                    <div className="btn_group justify-content-end gap_8 pb_50 edit_view_fixedbtn_d2">
                                        <button className='w_120_d2 btn btn_primary w_fit_content' onClick={handleSave}>
                                            <span>Finish</span><img src={loaderIcon} alt='USP' className='loader_icon' />
                                        </button>
                                    </div>
                                </>} */}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div >
    );
};

export default OnboardingStep3;
